import React, { Fragment, useEffect, useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { Container, Modal, ModalHeader, ModalBody, ModalFooter, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import Breadcrumb from "../common/breadcrumb";
import { C, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Loader } from "../reporting/ReportingShared";
import ToggleSwitch from "../../shared/ToggleSwitch";
import {
    getMerchantByID,
    getLoyaltyList,
    getWalletStripPreview,
    downloadTestPass,
    saveWalletDesign,
    fetchBranches,
    getWalletRefreshSummary,
    refreshAllWalletPasses,
} from "../../utils/apiCalls";
import OfferMessageCard, { DEFAULT_OFFER_MESSAGE } from "./offer-message-card";
import LocationAlertsCard, { DEFAULT_LOCATION_ALERTS } from "./location-alerts-card";
import AuditLogPanel from "../common/AuditLogPanel";
import { WALLET_AUDIT_ACTION_LABELS, WALLET_AUDIT_FIELD_LABELS, walletAuditResolve } from "../../constants/walletAudit";

// 1x1 transparent PNG — neutral placeholder before an icon is chosen.
const TRANSPARENT =
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==";

const DEFAULTS = {
    backgroundColor: "#FFFFFF",
    foregroundColor: "#000000",
    labelColor: "#828282",
    stripPrimaryColor: "#E8AB03",
    emptyTileColor: "#FFF7ED",
    stampFillOpacity: 100, // 100 = solid filled tiles; lower lets the bg image show through
    emptyTileOpacity: 100, // 100 = solid empty tiles; lower lets the bg image show through
    stripOverlayColor: "#000000",
    stripOverlayOpacity: 0, // 0 = no overlay/scrim over the strip background image
    stampRows: 0, // 0 = auto-fit rows for the largest tile; 1/2/3 forces that many rows
    stampRowGap: 10, // px gap between rows
    stampColGap: 10, // px gap between tiles in a row
    stampPadTop: 12, // px padding above the grid
    stampPadBottom: 12, // px padding below the grid
    stampIconScale: 86, // icon size as a % of the tile
    showLogo: true, // top-left logo image on the card face (optional per Apple)
    showLogoText: true, // top-left title text (merchant name) next to the logo
    headerRightText: "", // top-right header text; empty = live stamps count (e.g. 6/7)
};

const ASSET_BASE = process.env.REACT_APP_URL || "";

// ── small styled inputs ─────────────────────────────────────────
const ColorField = ({ label, hint, value, onChange }) => {
    const v = value || "#ffffff";
    return (
        <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 6 }}>{label}</div>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", margin: 0 }}>
                <div style={{ width: 40, height: 40, borderRadius: 9, background: v, border: `2px solid ${C.border}`, boxShadow: C.shadow, flexShrink: 0, position: "relative" }}>
                    <input
                        type="color"
                        value={v}
                        onChange={(e) => onChange(e.target.value)}
                        style={{ position: "absolute", inset: 0, opacity: 0, width: "100%", height: "100%", cursor: "pointer" }}
                    />
                </div>
                <div style={{ padding: "8px 12px", borderRadius: 8, background: C.bg, border: `1px solid ${C.borderLight}`, fontWeight: 600, color: C.textMed, fontSize: 12, letterSpacing: 0.5 }}>
                    {v.toUpperCase()}
                </div>
            </label>
            {hint && <div style={{ fontSize: 11, color: C.textLight, marginTop: 5 }}>{hint}</div>}
        </div>
    );
};

const SliderField = ({ label, hint, value, onChange, min = 0, max = 100, suffix = "%" }) => (
    <div>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 8 }}>
            {label} — {value}{suffix}
        </div>
        <input
            type="range" min={min} max={max} value={value}
            onChange={(e) => onChange(parseInt(e.target.value, 10))}
            style={{ width: "100%", accentColor: C.coral }}
        />
        {hint && <div style={{ fontSize: 11, color: C.textLight, marginTop: 5, lineHeight: 1.4 }}>{hint}</div>}
    </div>
);

const ImageUploadField = ({ label, hint, preview, onFile }) => (
    <div>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 6 }}>{label}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 72, height: 72, borderRadius: 10, border: `2px dashed ${C.border}`, background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
                <img src={preview || TRANSPARENT} alt={label} style={{ width: "100%", height: "100%", objectFit: "contain" }} onError={(e) => { e.target.src = TRANSPARENT; }} />
            </div>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 14px", borderRadius: 10, background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, color: C.white, fontWeight: 600, fontSize: 12, cursor: "pointer", boxShadow: "0 4px 12px rgba(0,150,137,0.25)" }}>
                Choose…
                <input type="file" accept="image/*" onChange={onFile} style={{ display: "none" }} />
            </label>
        </div>
        {hint && <div style={{ fontSize: 11, color: C.textLight, marginTop: 5, lineHeight: 1.4 }}>{hint}</div>}
    </div>
);

// ── Apple Wallet card mockup ────────────────────────────────────
// Chrome (logo / fields / colors) is pure CSS from live values → instant.
// The strip is the real generateStampStrip PNG fetched from the backend.
const PassField = ({ label, value, labelColor, fgColor, align = "left" }) => (
    <div style={{ textAlign: align, minWidth: 0 }}>
        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.6, color: labelColor, textTransform: "uppercase", whiteSpace: "nowrap" }}>{label}</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: fgColor, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
    </div>
);

const WalletCardPreview = ({ colors, isStamps, totalStamps, stampsCollected, merchantName, logoUrl, showLogo = true, showLogoText = true, headerRightText = "", stripUrl, stripLoading, customerName, offerText = "", offerDir }) => {
    const rewardsReady = isStamps ? Math.floor(stampsCollected / Math.max(1, totalStamps)) : 0;
    return (
        <div style={{ width: 340, maxWidth: "100%", borderRadius: 16, overflow: "hidden", background: colors.backgroundColor, boxShadow: "0 18px 40px rgba(15,23,42,0.22)", border: `1px solid ${C.border}`, fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif" }}>
            {/* header: logo + name (left), primary stat (right) */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px 10px", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    {showLogo && (
                        <div style={{ width: 28, height: 28, borderRadius: 6, overflow: "hidden", background: "rgba(0,0,0,0.06)", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <img src={logoUrl || TRANSPARENT} alt="logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} onError={(e) => { e.target.src = TRANSPARENT; }} />
                        </div>
                    )}
                    {showLogoText && (
                        <div style={{ fontSize: 13, fontWeight: 600, color: colors.foregroundColor, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{merchantName || "Merchant"}</div>
                    )}
                </div>
                {headerRightText
                    ? <PassField label="" value={headerRightText} labelColor={colors.labelColor} fgColor={colors.foregroundColor} align="right" />
                    : isStamps
                        ? <PassField label="Stamps" value={`${stampsCollected}/${totalStamps}`} labelColor={colors.labelColor} fgColor={colors.foregroundColor} align="right" />
                        : <PassField label="Points" value="120" labelColor={colors.labelColor} fgColor={colors.foregroundColor} align="right" />}
            </div>

            {/* strip: the hero image */}
            <div style={{ width: "100%", aspectRatio: "1125 / 384", background: colors.backgroundColor, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {stripUrl
                    ? <img src={stripUrl} alt="strip" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", opacity: stripLoading ? 0.45 : 1, transition: "opacity 0.15s" }} />
                    : <div style={{ fontSize: 12, color: C.textLight, padding: "0 16px", textAlign: "center" }}>{isStamps ? "Rendering…" : "No strip image set for this percentage card."}</div>}
                {stripLoading && stripUrl && (
                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <div style={{ padding: "4px 10px", borderRadius: 20, background: "rgba(255,255,255,0.85)", fontSize: 11, fontWeight: 600, color: C.textMed }}>updating…</div>
                    </div>
                )}
            </div>

            {/* secondary row: the OFFER field — present only while an offer text is set */}
            {offerText ? (
                <div style={{ padding: "10px 16px 0" }} dir={offerDir}>
                    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.6, color: colors.labelColor, textTransform: "uppercase" }}>{offerDir === "rtl" ? "عرض" : "Offer"}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: colors.foregroundColor, lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{offerText}</div>
                </div>
            ) : null}

            {/* auxiliary row */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "10px 16px 4px", gap: 10 }}>
                {isStamps
                    ? <PassField label="Rewards" value={String(rewardsReady)} labelColor={colors.labelColor} fgColor={colors.foregroundColor} />
                    : <PassField label="Tier" value="Gold" labelColor={colors.labelColor} fgColor={colors.foregroundColor} />}
                <PassField label="Hello" value={customerName} labelColor={colors.labelColor} fgColor={colors.foregroundColor} align="right" />
            </div>

            {/* barcode */}
            <div style={{ padding: "12px 16px 18px", display: "flex", justifyContent: "center" }}>
                <div style={{ width: 92, height: 92, borderRadius: 8, background: "#fff", border: `1px solid ${C.border}`, display: "grid", gridTemplateColumns: "repeat(5,1fr)", gridTemplateRows: "repeat(5,1fr)", padding: 8, gap: 2 }}>
                    {Array.from({ length: 25 }).map((_, i) => (
                        <div key={i} style={{ background: (i * 7 + 3) % 3 === 0 ? "#111" : "transparent", borderRadius: 1 }} />
                    ))}
                </div>
            </div>
        </div>
    );
};

// ── page ────────────────────────────────────────────────────────
const LoyaltyCardDesign = () => {
    const userType = localStorage.getItem("userType");
    const loggedInMerchantId = localStorage.getItem("MerchantId");
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
    const merchantId = userType === "merchant" ? loggedInMerchantId : reduxSelectedMerchant;

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [downloadingPass, setDownloadingPass] = useState(false);

    const [colors, setColors] = useState(DEFAULTS);
    const [merchantName, setMerchantName] = useState("");
    const [logoUrl, setLogoUrl] = useState("");

    const [isStamps, setIsStamps] = useState(true);
    const [totalStamps, setTotalStamps] = useState(10);
    const [stampsCollected, setStampsCollected] = useState(6);

    // icon files (new) + preview URLs (existing saved or new object URL)
    const [iconFiles, setIconFiles] = useState({}); // { walletPassIcon, walletPassStrip, walletPassStampFilledIcon, walletPassStampEmptyIcon }
    const [iconPreviews, setIconPreviews] = useState({});

    const [stripUrl, setStripUrl] = useState("");
    const [stripLoading, setStripLoading] = useState(false);

    // Offer text + near-branch suggestion (both live in walletPassConfig alongside the colors).
    const [offerMessage, setOfferMessage] = useState(DEFAULT_OFFER_MESSAGE);
    const [locationAlerts, setLocationAlerts] = useState(DEFAULT_LOCATION_ALERTS);
    const [branches, setBranches] = useState([]);
    const [branchesLoading, setBranchesLoading] = useState(false);
    const [previewLang, setPreviewLang] = useState("en");

    // "Push update to all wallets": reach + cap summary, confirm dialog, in-flight flag.
    const [summary, setSummary] = useState(null);
    const [pushing, setPushing] = useState(false);
    const [confirmPush, setConfirmPush] = useState(false);
    // Unsaved edits: the push sends what is SAVED, so it is disabled while this is true.
    const [dirty, setDirty] = useState(false);
    // Bumped after a save or push so the history panel remounts and shows the new row.
    const [historyKey, setHistoryKey] = useState(0);
    // "design" (colours, images, layout) | "alerts" (offer text + near-branch suggestion).
    // Both tabs edit the same walletPassConfig, so Save / Push / preview sit below the tabs.
    const [activeTab, setActiveTab] = useState("design");

    const setColor = (key, val) => { setColors((c) => ({ ...c, [key]: val })); setDirty(true); };
    const changeOffer = (next) => { setOfferMessage(next); setDirty(true); };
    const changeLocationAlerts = (next) => { setLocationAlerts(next); setDirty(true); };

    const reloadSummary = useCallback(async () => {
        if (!merchantId || merchantId === "all") return;
        setSummary(await getWalletRefreshSummary(merchantId));
    }, [merchantId]);

    // object URLs we created and must revoke to avoid leaks
    const stripObjUrl = useRef(null);
    const iconObjUrls = useRef([]);

    // ── load merchant config + active loyalty ──
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            if (!merchantId || merchantId === "all") return;
            setLoading(true);
            setBranchesLoading(true);
            try {
                const [merchantRes, loyalties, branchesRes, summaryRes] = await Promise.all([
                    getMerchantByID(merchantId),
                    getLoyaltyList(merchantId),
                    fetchBranches({ merchantId }, true),
                    getWalletRefreshSummary(merchantId),
                ]);
                if (cancelled) return;
                setBranches(Array.isArray(branchesRes?.data) ? branchesRes.data : []);
                setBranchesLoading(false);
                setSummary(summaryRes);

                const data = merchantRes?.data || {};
                let cfg = {};
                if (data.walletPassConfig && typeof data.walletPassConfig === "string") {
                    try { cfg = JSON.parse(data.walletPassConfig); } catch { cfg = {}; }
                } else if (data.walletPassConfig) {
                    cfg = data.walletPassConfig;
                }

                setColors({
                    backgroundColor: cfg.backgroundColor || DEFAULTS.backgroundColor,
                    foregroundColor: cfg.foregroundColor || DEFAULTS.foregroundColor,
                    labelColor: cfg.labelColor || DEFAULTS.labelColor,
                    stripPrimaryColor: cfg.stripPrimaryColor || data.menuColor || DEFAULTS.stripPrimaryColor,
                    emptyTileColor: cfg.emptyTileColor || DEFAULTS.emptyTileColor,
                    stampFillOpacity: cfg.stampFillOpacity ?? DEFAULTS.stampFillOpacity,
                    emptyTileOpacity: cfg.emptyTileOpacity ?? DEFAULTS.emptyTileOpacity,
                    stripOverlayColor: cfg.stripOverlayColor || DEFAULTS.stripOverlayColor,
                    stripOverlayOpacity: cfg.stripOverlayOpacity ?? DEFAULTS.stripOverlayOpacity,
                    stampRows: cfg.stampRows ?? DEFAULTS.stampRows,
                    stampRowGap: cfg.stampRowGap ?? DEFAULTS.stampRowGap,
                    stampColGap: cfg.stampColGap ?? DEFAULTS.stampColGap,
                    stampPadTop: cfg.stampPadTop ?? DEFAULTS.stampPadTop,
                    stampPadBottom: cfg.stampPadBottom ?? DEFAULTS.stampPadBottom,
                    stampIconScale: cfg.stampIconScale ?? DEFAULTS.stampIconScale,
                    showLogo: cfg.showLogo ?? DEFAULTS.showLogo,
                    showLogoText: cfg.showLogoText ?? DEFAULTS.showLogoText,
                    headerRightText: cfg.headerRightText ?? DEFAULTS.headerRightText,
                });
                setMerchantName(data.name || "");
                setLogoUrl(data.image1 ? ASSET_BASE + data.image1 : "");
                setIconPreviews({
                    walletPassIcon: data.walletPassIcon ? ASSET_BASE + data.walletPassIcon : "",
                    walletPassStrip: data.walletPassStrip ? ASSET_BASE + data.walletPassStrip : "",
                    walletPassStampFilledIcon: data.walletPassStampFilledIcon ? ASSET_BASE + data.walletPassStampFilledIcon : "",
                    walletPassStampEmptyIcon: data.walletPassStampEmptyIcon ? ASSET_BASE + data.walletPassStampEmptyIcon : "",
                    walletPassStripBackground: data.walletPassStripBackground ? ASSET_BASE + data.walletPassStripBackground : "",
                });
                setIconFiles({});
                setOfferMessage({ ...DEFAULT_OFFER_MESSAGE, ...(cfg.offerMessage || {}) });
                setLocationAlerts({ ...DEFAULT_LOCATION_ALERTS, ...(cfg.locationAlerts || {}) });
                setDirty(false);

                // Seed stamp count + type from the active loyalty (fall back to first).
                const list = Array.isArray(loyalties) ? loyalties : [];
                const active = list.find((l) => l.isActive) || list[0];
                if (active) {
                    const stamps = active.loyaltyType === "stamps";
                    setIsStamps(stamps);
                    if (stamps && active.rewardOnStampsCount > 0) {
                        const total = active.rewardOnStampsCount;
                        setTotalStamps(total);
                        setStampsCollected(Math.max(1, Math.round(total * 0.6)));
                    }
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [merchantId]);

    // ── debounced real-strip render (stamp cards only) ──
    useEffect(() => {
        // Percentage cards use a static strip (handled below) — make sure we
        // never leave the "updating…" flag stuck on when we skip the fetch.
        if (!merchantId || merchantId === "all" || !isStamps) { setStripLoading(false); return; }
        let cancelled = false;
        setStripLoading(true);
        const t = setTimeout(async () => {
            const blob = await getWalletStripPreview(
                merchantId,
                {
                    stampsCollected,
                    totalStamps,
                    stripPrimaryColor: colors.stripPrimaryColor,
                    backgroundColor: colors.backgroundColor,
                    emptyTileColor: colors.emptyTileColor,
                    stampFillOpacity: colors.stampFillOpacity,
                    emptyTileOpacity: colors.emptyTileOpacity,
                    stripOverlayColor: colors.stripOverlayColor,
                    stripOverlayOpacity: colors.stripOverlayOpacity,
                    stampRows: colors.stampRows,
                    stampRowGap: colors.stampRowGap,
                    stampColGap: colors.stampColGap,
                    stampPadTop: colors.stampPadTop,
                    stampPadBottom: colors.stampPadBottom,
                    stampIconScale: colors.stampIconScale,
                },
                {
                    walletPassStampFilledIcon: iconFiles.walletPassStampFilledIcon,
                    walletPassStampEmptyIcon: iconFiles.walletPassStampEmptyIcon,
                    walletPassStripBackground: iconFiles.walletPassStripBackground,
                }
            );
            if (cancelled || !blob) { if (!cancelled) setStripLoading(false); return; }
            const url = URL.createObjectURL(blob);
            if (stripObjUrl.current) URL.revokeObjectURL(stripObjUrl.current);
            stripObjUrl.current = url;
            setStripUrl(url);
            setStripLoading(false);
        }, 300);
        return () => { cancelled = true; clearTimeout(t); };
    }, [
        merchantId, isStamps, stampsCollected, totalStamps,
        colors.stripPrimaryColor, colors.backgroundColor, colors.emptyTileColor,
        colors.stampFillOpacity, colors.emptyTileOpacity,
        colors.stripOverlayColor, colors.stripOverlayOpacity,
        colors.stampRows, colors.stampRowGap, colors.stampColGap, colors.stampPadTop, colors.stampPadBottom, colors.stampIconScale,
        iconFiles.walletPassStampFilledIcon, iconFiles.walletPassStampEmptyIcon, iconFiles.walletPassStripBackground,
    ]);

    // For percentage cards the strip is the static uploaded image, not dynamic.
    useEffect(() => {
        if (isStamps) return;
        setStripUrl(iconPreviews.walletPassStrip || "");
    }, [isStamps, iconPreviews.walletPassStrip]);

    // revoke any object URLs on unmount
    useEffect(() => () => {
        if (stripObjUrl.current) URL.revokeObjectURL(stripObjUrl.current);
        iconObjUrls.current.forEach((u) => URL.revokeObjectURL(u));
    }, []);

    const handleIconFile = (key) => (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const url = URL.createObjectURL(file);
        iconObjUrls.current.push(url);
        setIconFiles((f) => ({ ...f, [key]: file }));
        setIconPreviews((p) => ({ ...p, [key]: url }));
        setDirty(true);
    };

    // Only the editable keys go to the server; the bookkeeping (updatedAt, lastBroadcast*)
    // is owned by the API and comes back in the response.
    const offerPayload = { text: offerMessage.text || "", textAr: offerMessage.textAr || "" };
    const locationPayload = {
        enabled: Boolean(locationAlerts.enabled),
        text: locationAlerts.text || "",
        textAr: locationAlerts.textAr || "",
        maxDistanceM: locationAlerts.maxDistanceM === "" || locationAlerts.maxDistanceM === undefined ? null : locationAlerts.maxDistanceM,
        branchMode: locationAlerts.branchMode === "manual" ? "manual" : "auto",
        branchIds: locationAlerts.branchIds || [],
        branchTexts: locationAlerts.branchTexts || {},
    };

    const handlePushAll = async () => {
        if (!merchantId || merchantId === "all") return;
        setPushing(true);
        try {
            const result = await refreshAllWalletPasses(merchantId);
            if (result) {
                setConfirmPush(false);
                setHistoryKey((k) => k + 1);
                // The push may have stamped lastBroadcast* on the saved offer — reload it.
                const merchantRes = await getMerchantByID(merchantId);
                const data = merchantRes?.data || {};
                let cfg = {};
                if (data.walletPassConfig && typeof data.walletPassConfig === "string") {
                    try { cfg = JSON.parse(data.walletPassConfig); } catch { cfg = {}; }
                } else if (data.walletPassConfig) {
                    cfg = data.walletPassConfig;
                }
                setOfferMessage({ ...DEFAULT_OFFER_MESSAGE, ...(cfg.offerMessage || {}) });
            }
            await reloadSummary();
        } finally {
            setPushing(false);
        }
    };

    const handleSave = async () => {
        if (!merchantId || merchantId === "all") return;
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append("dataType", "merchant");
            fd.append("walletPassConfig", JSON.stringify({
                backgroundColor: colors.backgroundColor,
                foregroundColor: colors.foregroundColor,
                labelColor: colors.labelColor,
                stripPrimaryColor: colors.stripPrimaryColor,
                emptyTileColor: colors.emptyTileColor,
                stampFillOpacity: colors.stampFillOpacity,
                emptyTileOpacity: colors.emptyTileOpacity,
                stripOverlayColor: colors.stripOverlayColor,
                stripOverlayOpacity: colors.stripOverlayOpacity,
                stampRows: colors.stampRows,
                stampRowGap: colors.stampRowGap,
                stampColGap: colors.stampColGap,
                stampPadTop: colors.stampPadTop,
                stampPadBottom: colors.stampPadBottom,
                stampIconScale: colors.stampIconScale,
                showLogo: colors.showLogo,
                showLogoText: colors.showLogoText,
                headerRightText: colors.headerRightText,
                offerMessage: offerPayload,
                locationAlerts: locationPayload,
            }));
            ["walletPassIcon", "walletPassStrip", "walletPassStampFilledIcon", "walletPassStampEmptyIcon", "walletPassStripBackground"].forEach((k) => {
                if (iconFiles[k] instanceof File) fd.append(k, iconFiles[k]);
            });
            const saved = await saveWalletDesign(merchantId, fd);
            if (saved?.walletPassConfig) {
                // Take the server's normalised values (trimmed text, updatedAt, dropped empties).
                setOfferMessage({ ...DEFAULT_OFFER_MESSAGE, ...(saved.walletPassConfig.offerMessage || {}) });
                setLocationAlerts({ ...DEFAULT_LOCATION_ALERTS, ...(saved.walletPassConfig.locationAlerts || {}) });
                setIconFiles({});
                setDirty(false);
                setHistoryKey((k) => k + 1);
                await reloadSummary();
            }
        } finally {
            setSaving(false);
        }
    };

    // Build a real signed .pkpass for a synthetic test customer and trigger a
    // browser download — open it on an iPhone to see the true on-device render.
    const handleDownloadTestPass = async () => {
        if (!merchantId || merchantId === "all") return;
        setDownloadingPass(true);
        try {
            const blob = await downloadTestPass(
                merchantId,
                {
                    stampsCollected,
                    totalStamps,
                    backgroundColor: colors.backgroundColor,
                    foregroundColor: colors.foregroundColor,
                    labelColor: colors.labelColor,
                    stripPrimaryColor: colors.stripPrimaryColor,
                    emptyTileColor: colors.emptyTileColor,
                    stampFillOpacity: colors.stampFillOpacity,
                    emptyTileOpacity: colors.emptyTileOpacity,
                    stripOverlayColor: colors.stripOverlayColor,
                    stripOverlayOpacity: colors.stripOverlayOpacity,
                    stampRows: colors.stampRows,
                    stampRowGap: colors.stampRowGap,
                    stampColGap: colors.stampColGap,
                    stampPadTop: colors.stampPadTop,
                    stampPadBottom: colors.stampPadBottom,
                    stampIconScale: colors.stampIconScale,
                    showLogo: colors.showLogo,
                    showLogoText: colors.showLogoText,
                    headerRightText: colors.headerRightText,
                    offerMessage: offerPayload,
                    locationAlerts: locationPayload,
                    previewLang,
                },
                {
                    walletPassIcon: iconFiles.walletPassIcon,
                    walletPassStrip: iconFiles.walletPassStrip,
                    walletPassStampFilledIcon: iconFiles.walletPassStampFilledIcon,
                    walletPassStampEmptyIcon: iconFiles.walletPassStampEmptyIcon,
                    walletPassStripBackground: iconFiles.walletPassStripBackground,
                }
            );
            if (!blob) return;
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "test-loyalty-card.pkpass";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } finally {
            setDownloadingPass(false);
        }
    };

    const presets = isStamps ? [
        { label: "Empty", v: 0 },
        { label: "Half", v: Math.floor(totalStamps / 2) },
        { label: "Almost", v: Math.max(0, totalStamps - 1) },
        { label: "Full", v: totalStamps },
    ] : [];

    const noMerchant = !merchantId || merchantId === "all";

    return (
        <Fragment>
            <Breadcrumb title="Loyalty Card Design" parent="Loyalty" />
            <Container fluid>
                <style>{reportingGlobalCSS}</style>
                <div className="rpt-wrap">
                    {noMerchant ? (
                        <RCard><div style={{ textAlign: "center", padding: 40, color: C.textMed }}>Please select a merchant to design its loyalty card.</div></RCard>
                    ) : loading ? (
                        <Loader />
                    ) : (
                        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 380px", gap: 20, alignItems: "start" }}>
                            {/* editor */}
                            <div>
                                <Nav tabs style={{ borderBottom: `1px solid ${C.border}`, marginBottom: 20 }}>
                                    <NavItem>
                                        <NavLink active={activeTab === "design"} onClick={() => setActiveTab("design")} style={{ cursor: "pointer", color: activeTab === "design" ? C.coral : C.textMed, fontWeight: 600 }}>
                                            🎨 Card design
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink active={activeTab === "alerts"} onClick={() => setActiveTab("alerts")} style={{ cursor: "pointer", color: activeTab === "alerts" ? C.coral : C.textMed, fontWeight: 600 }}>
                                            📣 Offer &amp; near-branch
                                            {summary?.broadcast?.willNotify ? <span title="Saved offer text not yet announced" style={{ marginLeft: 6, color: C.orange }}>●</span> : null}
                                        </NavLink>
                                    </NavItem>
                                </Nav>

                                <TabContent activeTab={activeTab}>
                                <TabPane tabId="design">
                                <RCard title="🎨 Colors">
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
                                        <ColorField label="Pass Background" value={colors.backgroundColor} onChange={(v) => setColor("backgroundColor", v)} />
                                        <ColorField label="Text (Foreground)" value={colors.foregroundColor} onChange={(v) => setColor("foregroundColor", v)} />
                                        <ColorField label="Label" value={colors.labelColor} onChange={(v) => setColor("labelColor", v)} />
                                        <ColorField label="Stamp / Accent" hint="Filled tiles + progress bar" value={colors.stripPrimaryColor} onChange={(v) => setColor("stripPrimaryColor", v)} />
                                        <ColorField label="Empty Stamp Tile" value={colors.emptyTileColor} onChange={(v) => setColor("emptyTileColor", v)} />
                                    </div>
                                </RCard>

                                <RCard title="🎫 Card Options">
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                                        <div>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Show Logo</div>
                                            <div style={{ fontSize: 11, color: C.textLight, marginTop: 4, maxWidth: 420, lineHeight: 1.4 }}>
                                                The image at the top-left of the card face. Optional — turn off to hide it (the merchant name text stays). Apple's required icon is not affected.
                                            </div>
                                        </div>
                                        <ToggleSwitch checked={colors.showLogo} handleToggle={() => setColor("showLogo", !colors.showLogo)} />
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginTop: 18, paddingTop: 18, borderTop: `1px solid ${C.borderLight}` }}>
                                        <div>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Show Title Text</div>
                                            <div style={{ fontSize: 11, color: C.textLight, marginTop: 4, maxWidth: 420, lineHeight: 1.4 }}>
                                                The merchant name shown next to the logo at the top-left of the card face. Turn off to hide it and leave only the logo image.
                                            </div>
                                        </div>
                                        <ToggleSwitch checked={colors.showLogoText} handleToggle={() => setColor("showLogoText", !colors.showLogoText)} />
                                    </div>
                                    <div style={{ marginTop: 18, paddingTop: 18, borderTop: `1px solid ${C.borderLight}` }}>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 6 }}>Top-Right Text</div>
                                        <input
                                            type="text"
                                            value={colors.headerRightText}
                                            onChange={(e) => setColor("headerRightText", e.target.value)}
                                            placeholder="Leave empty for live stamp count (e.g. 6/7)"
                                            style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: C.white, boxSizing: "border-box" }}
                                        />
                                        <div style={{ fontSize: 11, color: C.textLight, marginTop: 5, lineHeight: 1.4 }}>
                                            Text shown at the top-right of the card (e.g. <span dir="rtl">قهوة تُلهم</span>). Leave empty to show the live stamp count. Uses Apple's native font.
                                        </div>
                                    </div>
                                </RCard>

                                <RCard title="🖼️ Images">
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                                        <ImageUploadField label="Pass Icon" hint="Square icon, required by Apple. Transparent PNG recommended." preview={iconPreviews.walletPassIcon} onFile={handleIconFile("walletPassIcon")} />
                                        <ImageUploadField label="Filled Stamp Icon" hint="Inside collected tiles. White silhouette works best." preview={iconPreviews.walletPassStampFilledIcon} onFile={handleIconFile("walletPassStampFilledIcon")} />
                                        <ImageUploadField label="Empty Stamp Icon" hint="Inside remaining tiles." preview={iconPreviews.walletPassStampEmptyIcon} onFile={handleIconFile("walletPassStampEmptyIcon")} />
                                        <ImageUploadField label="Strip (Percentage cards)" hint="Static banner for non-stamp loyalty." preview={iconPreviews.walletPassStrip} onFile={handleIconFile("walletPassStrip")} />
                                    </div>
                                </RCard>

                                {isStamps && (
                                    <RCard title="🖼️ Strip Background">
                                        <div style={{ fontSize: 12, color: C.textMed, marginBottom: 16, lineHeight: 1.5 }}>
                                            Optional image shown <b>behind the stamp tiles</b>. Use a wide image — recommended <b>1125 × 384 px</b> (~3:1); it's scaled to fill the strip. One image is enough — no @2x/@3x versions needed. Leave empty to use the solid <b>Pass Background</b> color. Lower the tile opacities below to let the background read <i>through</i> the stamps.
                                        </div>
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, alignItems: "start" }}>
                                            <ImageUploadField label="Background Image" hint="Sits behind the stamp tiles." preview={iconPreviews.walletPassStripBackground} onFile={handleIconFile("walletPassStripBackground")} />
                                            <ColorField label="Overlay Tint" hint="Optional scrim over the image to keep tiles readable." value={colors.stripOverlayColor} onChange={(v) => setColor("stripOverlayColor", v)} />
                                        </div>
                                        <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20 }}>
                                            <SliderField label="Stamp Tile Opacity" hint="Lower lets the background show through filled tiles. Icon + border stay visible." value={colors.stampFillOpacity} onChange={(v) => setColor("stampFillOpacity", v)} />
                                            <SliderField label="Empty Tile Opacity" hint="Lower lets the background show through empty tiles." value={colors.emptyTileOpacity} onChange={(v) => setColor("emptyTileOpacity", v)} />
                                            <SliderField label="Overlay Opacity" hint="0% = no overlay (image as-is)." value={colors.stripOverlayOpacity} onChange={(v) => setColor("stripOverlayOpacity", v)} />
                                        </div>
                                    </RCard>
                                )}

                                {isStamps && (
                                    <RCard title="🔲 Stamp Layout">
                                        <div style={{ fontSize: 12, color: C.textMed, marginBottom: 16, lineHeight: 1.5 }}>
                                            Control how the stamp tiles are arranged and sized on the strip.
                                        </div>
                                        <div style={{ marginBottom: 18 }}>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 8 }}>Rows</div>
                                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                                {[{ label: "Auto", v: 0 }, { label: "1", v: 1 }, { label: "2", v: 2 }, { label: "3", v: 3 }].map((r) => (
                                                    <button key={r.label} onClick={() => setColor("stampRows", r.v)} style={{ padding: "6px 16px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer", border: `1px solid ${colors.stampRows === r.v ? C.coral : C.border}`, background: colors.stampRows === r.v ? C.coralLight : C.white, color: colors.stampRows === r.v ? C.coralDark : C.textMed }}>
                                                        {r.label}
                                                    </button>
                                                ))}
                                            </div>
                                            <div style={{ fontSize: 11, color: C.textLight, marginTop: 6 }}>Auto picks the row count that makes tiles largest (e.g. 8 stamps → 2 rows of 4).</div>
                                        </div>
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                                            <SliderField label="Icon Size" min={30} max={100} value={colors.stampIconScale} onChange={(v) => setColor("stampIconScale", v)} hint="How much of each tile the picture fills." />
                                            <SliderField label="Row Gap" min={0} max={100} suffix="px" value={colors.stampRowGap} onChange={(v) => setColor("stampRowGap", v)} hint="Vertical space between rows." />
                                            <SliderField label="Column Gap" min={0} max={100} suffix="px" value={colors.stampColGap} onChange={(v) => setColor("stampColGap", v)} hint="Horizontal space between tiles." />
                                            <SliderField label="Padding Top" min={0} max={150} suffix="px" value={colors.stampPadTop} onChange={(v) => setColor("stampPadTop", v)} hint="Space above the grid." />
                                            <SliderField label="Padding Bottom" min={0} max={150} suffix="px" value={colors.stampPadBottom} onChange={(v) => setColor("stampPadBottom", v)} hint="Space below the grid." />
                                        </div>
                                    </RCard>
                                )}

                                {isStamps && (
                                    <RCard title="🔢 Preview State">
                                        <div style={{ fontSize: 12, color: C.textMed, marginBottom: 14 }}>
                                            Drag to preview the card at different stamp counts. This only affects the preview — it doesn't change customer data.
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                                            <input type="range" min={0} max={totalStamps} value={stampsCollected} onChange={(e) => setStampsCollected(parseInt(e.target.value, 10))} style={{ flex: 1, minWidth: 200, accentColor: C.coral }} />
                                            <div style={{ fontWeight: 700, color: C.text, fontSize: 14, minWidth: 64, textAlign: "right" }}>{stampsCollected} / {totalStamps}</div>
                                        </div>
                                        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                                            {presets.map((p) => (
                                                <button key={p.label} onClick={() => setStampsCollected(p.v)} style={{ padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer", border: `1px solid ${stampsCollected === p.v ? C.coral : C.border}`, background: stampsCollected === p.v ? C.coralLight : C.white, color: stampsCollected === p.v ? C.coralDark : C.textMed }}>
                                                    {p.label}
                                                </button>
                                            ))}
                                        </div>
                                    </RCard>
                                )}

                                </TabPane>

                                <TabPane tabId="alerts">
                                <OfferMessageCard
                                    value={offerMessage}
                                    onChange={changeOffer}
                                    merchantName={merchantName}
                                    summary={summary}
                                />

                                <LocationAlertsCard
                                    value={locationAlerts}
                                    onChange={changeLocationAlerts}
                                    branches={branches}
                                    branchesLoading={branchesLoading}
                                    offerText={offerMessage.text || offerMessage.textAr || ""}
                                    merchantName={merchantName}
                                />
                                </TabPane>
                                </TabContent>

                                <RCard>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                                        <div style={{ fontSize: 12, color: C.textMed, lineHeight: 1.5, flex: 1, minWidth: 220 }}>
                                            <b>Save Design</b> stores the card for this merchant; new downloads get it at once. Installed cards refresh when the customer's balance changes, or immediately when you <b>push an update to all wallets</b>.
                                            <div style={{ marginTop: 6, color: C.textLight }}>
                                                <b>Download test pass</b> builds a real signed card for a dummy "Test Customer" with the offer and near-branch settings as they stand on this page — open it on an iPhone to see the exact on-device render. Nothing is saved.
                                            </div>
                                        </div>
                                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                            <button onClick={handleDownloadTestPass} disabled={downloadingPass} style={{ padding: "11px 20px", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: downloadingPass ? "default" : "pointer", background: C.white, color: C.text, border: `1.5px solid ${C.border}`, opacity: downloadingPass ? 0.7 : 1 }}>
                                                {downloadingPass ? "Generating…" : "⬇️ Download test .pkpass"}
                                            </button>
                                            <button onClick={handleSave} disabled={saving} style={{ padding: "11px 26px", borderRadius: 10, fontSize: 13, fontWeight: 700, border: "none", cursor: saving ? "default" : "pointer", background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, color: C.white, boxShadow: "0 4px 12px rgba(0,150,137,0.25)", opacity: saving ? 0.7 : 1 }}>
                                                {saving ? "Saving…" : dirty ? "💾 Save Design •" : "💾 Save Design"}
                                            </button>
                                        </div>
                                    </div>
                                </RCard>

                                <RCard title="📲 Push update to all wallets">
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                                        <div style={{ fontSize: 12, color: C.textMed, lineHeight: 1.55, flex: 1, minWidth: 220 }}>
                                            Sends a silent refresh to every device holding this merchant's card; iOS re-downloads the card in the background
                                            (usually within a minute, sometimes longer). If the <b>offer text changed</b> since the last announcement,
                                            card holders see it as a notification — that counts against the weekly limit. Design and near-branch changes alone are silent.
                                            <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: "4px 18px", color: C.textLight }}>
                                                <span>Installed cards: <b style={{ color: C.text }}>{summary ? summary.installed : "…"}</b> on <b style={{ color: C.text }}>{summary ? summary.devices : "…"}</b> device{summary?.devices === 1 ? "" : "s"}</span>
                                                {summary?.broadcast ? (
                                                    <span>
                                                        Offer announcements this week: <b style={{ color: summary.broadcast.capReached ? C.coral : C.text }}>{summary.broadcast.usedThisWeek} of {summary.broadcast.weeklyLimit}</b>
                                                        {summary.broadcast.nextAllowedAt ? <span> · next slot frees {new Date(summary.broadcast.nextAllowedAt).toLocaleString()}</span> : null}
                                                    </span>
                                                ) : null}
                                            </div>
                                            {dirty ? <div style={{ marginTop: 6, color: C.coral, fontWeight: 600 }}>You have unsaved changes — Save Design first; the push sends what is saved.</div> : null}
                                            {!dirty && summary?.broadcast?.blocked ? <div style={{ marginTop: 6, color: C.coral, fontWeight: 600 }}>Weekly announcement limit reached — the new offer text cannot be pushed until a slot frees. Design-only changes can still be pushed once the offer text is reverted.</div> : null}
                                        </div>
                                        <button
                                            onClick={() => setConfirmPush(true)}
                                            disabled={pushing || dirty || !summary || summary.devices === 0 || summary.broadcast?.blocked}
                                            style={{ padding: "11px 22px", borderRadius: 10, fontSize: 13, fontWeight: 700, border: "none", cursor: (pushing || dirty || !summary || summary.devices === 0 || summary.broadcast?.blocked) ? "default" : "pointer", background: `linear-gradient(135deg, ${C.orange}, #FFB347)`, color: C.white, boxShadow: "0 4px 12px rgba(247,151,30,0.25)", opacity: (pushing || dirty || !summary || summary.devices === 0 || summary.broadcast?.blocked) ? 0.55 : 1 }}
                                        >
                                            {pushing ? "Pushing…" : `📲 Push update to all wallets${summary ? ` (${summary.devices})` : ""}`}
                                        </button>
                                    </div>
                                </RCard>

                                <AuditLogPanel
                                    key={historyKey}
                                    merchantId={merchantId}
                                    module="wallet"
                                    title="📜 Card change history"
                                    actionLabels={WALLET_AUDIT_ACTION_LABELS}
                                    fieldLabels={WALLET_AUDIT_FIELD_LABELS}
                                    resolve={walletAuditResolve}
                                    defaultLimit={10}
                                />
                            </div>

                            {/* live preview (sticky) */}
                            <div style={{ position: "sticky", top: 20 }}>
                                <RCard title="📱 Live Preview">
                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "8px 0" }}>
                                        <WalletCardPreview
                                            colors={colors}
                                            isStamps={isStamps}
                                            totalStamps={totalStamps}
                                            stampsCollected={stampsCollected}
                                            merchantName={merchantName}
                                            logoUrl={logoUrl}
                                            showLogo={colors.showLogo}
                                            showLogoText={colors.showLogoText}
                                            headerRightText={colors.headerRightText}
                                            stripUrl={stripUrl}
                                            stripLoading={stripLoading}
                                            customerName={previewLang === "ar" ? "سارة" : "Sara"}
                                            offerText={previewLang === "ar" ? (offerMessage.textAr || offerMessage.text) : (offerMessage.text || offerMessage.textAr)}
                                            offerDir={previewLang === "ar" ? "rtl" : undefined}
                                        />
                                        <div style={{ display: "flex", gap: 8 }}>
                                            {[{ v: "en", label: "English" }, { v: "ar", label: "العربية" }].map((o) => (
                                                <button key={o.v} type="button" onClick={() => setPreviewLang(o.v)} style={{ padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer", border: `1px solid ${previewLang === o.v ? C.coral : C.border}`, background: previewLang === o.v ? C.coralLight : C.white, color: previewLang === o.v ? C.coralDark : C.textMed }}>
                                                    {o.label}
                                                </button>
                                            ))}
                                        </div>
                                        <div style={{ fontSize: 11, color: C.textLight, textAlign: "center", maxWidth: 300, lineHeight: 1.5 }}>
                                            The stamp strip is rendered by the same engine as the real Apple Wallet pass, so this is exactly what customers download. The language toggle also applies to the test pass.
                                        </div>
                                    </div>
                                </RCard>
                            </div>
                        </div>
                    )}
                </div>
            </Container>

            {/* Confirm: push update to all wallets */}
            <Modal isOpen={confirmPush} toggle={() => !pushing && setConfirmPush(false)}>
                <ModalHeader toggle={() => !pushing && setConfirmPush(false)}>Push update to all wallets?</ModalHeader>
                <ModalBody style={{ color: C.textMed, lineHeight: 1.6 }}>
                    <p style={{ margin: 0 }}>
                        This refreshes <strong style={{ color: C.text }}>{summary?.installed ?? 0}</strong> installed card{summary?.installed === 1 ? "" : "s"} on{" "}
                        <strong style={{ color: C.text }}>{summary?.devices ?? 0}</strong> device{summary?.devices === 1 ? "" : "s"}. iOS downloads the updated card in the background — usually within a minute, sometimes longer.
                    </p>
                    {summary?.broadcast?.willNotify ? (
                        <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 8, background: C.orangeLight, color: C.orange, fontSize: 13 }}>
                            <b>Customers will get a notification</b> with the new offer text:
                            <div style={{ marginTop: 4, color: C.text }}>“{summary.broadcast.offerText || summary.broadcast.offerTextAr}”</div>
                            <div style={{ marginTop: 6, fontSize: 12 }}>
                                This uses <b>1 of {summary.broadcast.weeklyLimit}</b> offer announcements this week ({summary.broadcast.usedThisWeek} used so far).
                            </div>
                        </div>
                    ) : (
                        <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 8, background: C.bg, fontSize: 13 }}>
                            This refresh is <b>silent</b> — nothing pops up on customers' phones. The card's design and near-branch settings update quietly.
                        </div>
                    )}
                </ModalBody>
                <ModalFooter style={{ borderTop: `1px solid ${C.border}` }}>
                    <button onClick={() => setConfirmPush(false)} disabled={pushing} style={{ padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", background: C.white, color: C.textMed, border: `1px solid ${C.border}` }}>Cancel</button>
                    <button onClick={handlePushAll} disabled={pushing} style={{ padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700, border: "none", cursor: pushing ? "default" : "pointer", background: `linear-gradient(135deg, ${C.orange}, #FFB347)`, color: C.white, boxShadow: "0 4px 12px rgba(247,151,30,0.25)", opacity: pushing ? 0.7 : 1 }}>
                        {pushing ? "Pushing…" : summary?.broadcast?.willNotify ? "Push and notify" : "Push silently"}
                    </button>
                </ModalFooter>
            </Modal>
        </Fragment>
    );
};

export default LoyaltyCardDesign;
